import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Coins,
  ChevronDown,
  ChevronRight,
  X,
  BarChart2,
  Star,
  MessageSquare,
  CalendarDays,
  Disc3,
} from 'lucide-react';
import { useAuth } from '../AuthModule';
import EventTypeTitle from '../EventTypeTitle';
import ProfileNavTopBar from '../ProfileNavTopBar';
import { useT } from '../../i18n/I18nModule';
import { loadingPageText } from '../../i18n/loadingPage';
import { useStatisticsUnlock, useCreditProductUnlocked } from '../../useStatisticsUnlock';
import { STATISTICS_ADVANCED_UNLOCK_PRODUCT_ID } from './profileHelpers';

const API = process.env.REACT_APP_API_URL || '';

/** Trim + translate reason for display: remove "PC Pulina Credit: " prefix, backend-only IDs/suffixes, and show Finnish labels. */
function displayReason(reason) {
  if (typeof reason !== 'string') return reason;
  let s = reason.replace(/^PC Pulina Credit:\s*/i, '').trim();

  // Strip any technical suffixes like :eventId, :questionId:eventId
  s = s.split(':')[0].trim();

  // Map known reasons to Finnish
  if (/^daily login/i.test(s)) return 'päivittäinen kirjautuminen';
  if (/^event host full participation/i.test(s)) return 'tapahtumajärjestäjä täysin läsnä';
  if (/^rate event/i.test(s)) return 'tapahtuman arviointi';
  if (/^event game TOP1/i.test(s)) return 'TOP1 pelissä';
  if (/^whispering to someone/i.test(s)) return 'kuiskaus toiselle käyttäjälle';
  if (/^mentioning someone/i.test(s)) return 'Käyttäjän maininta chatissä';
  if (/^submit question to event/i.test(s)) return 'kysymyksen lähetys tapahtumaan';
  if (/^question approved by author/i.test(s)) return 'tapahtuman järjestäjä hyväksyi kysymyksen';
  if (/^create event with video/i.test(s)) return 'Videollisen tapahtuman luonti';
  if (/^create event with game/i.test(s)) return 'Pelitapahtuman luonti.';
  if (/^rate playlist/i.test(s)) return '100% Levyraati arvostelu';
  if (/^trivia suggestion submission/i.test(s)) return 'tietovisaehdotuksen lähetys';
  if (/^trivia suggestion refund \(duplicate\)/i.test(s)) return 'tietovisaehdotus: hyvitys (duplikaatti)';
  if (/esittämäsi ehdotus oli jo meillä/i.test(s)) return 'tietovisaehdotus: hyvitys (duplikaatti)';

  return s || reason;
}

/** Credits history row: +N for gains, plain N (with minus) for losses. */
function formatCreditEarned(earned) {
  const n = Number(earned);
  if (!Number.isFinite(n)) return earned == null ? '' : String(earned);
  return n < 0 ? String(n) : `+${n}`;
}

function creditAmountToneClass(earned) {
  const n = Number(earned);
  if (!Number.isFinite(n)) return '';
  if (n < 0) return 'Tilastot-credits-amount--negative';
  if (n > 0) return 'Tilastot-credits-amount--positive';
  return '';
}

/** DD.mm.YYYY from ISO datetime (calendar date). */
function formatForumPostDate(isoDate) {
  if (!isoDate || !/^\d{4}-\d{2}-\d{2}/.test(isoDate)) return '—';
  const datePart = isoDate.slice(0, 10);
  const [y, m, d] = datePart.split('-').map((x) => parseInt(x, 10));
  if (!y || !m || !d) return '—';
  return `${String(d).padStart(2, '0')}.${String(m).padStart(2, '0')}.${y}`;
}

/** Single-line body snippet for list rows. */
function truncateForumBody(body, maxLen = 20) {
  const s = String(body || '')
    .replace(/\s+/g, ' ')
    .trim();
  if (!s) return '—';
  if (s.length <= maxLen) return s;
  return `${s.slice(0, maxLen)}...`;
}

const FORUM_POSTS_PAGE_SIZE = 25;
const COLLAPSIBLE_PAGE_SIZE = 40;

function formatStatsForumPostsHeader(total, t) {
  if (total === 1) return t('stats.forumPostsOne');
  return `${total} ${t('stats.forumPostsUnit')}`;
}

function formatStatsDailyPollsHeader(count, t) {
  const n = Number(count);
  if (!Number.isFinite(n) || n < 0) return `0 ${t('stats.dailyPollsUnit')}`;
  if (n === 1) return t('stats.dailyPollsOne');
  return `${n} ${t('stats.dailyPollsUnit')}`;
}

function formatStatsMusicReviewsHeader(count, t) {
  const n = Number(count);
  if (!Number.isFinite(n) || n < 0) return `0 ${t('stats.musicReviewsUnit')}`;
  if (n === 1) return t('stats.musicReviewsOne');
  return `${n} ${t('stats.musicReviewsUnit')}`;
}

function formatStatsEventsCount(count, t) {
  return `${count} ${t('stats.eventsUnit')}`;
}

function TilastotCollapsibleHeader({ collapsible, collapsed, onToggle, listId, children }) {
  if (collapsible) {
    return (
      <button
        type="button"
        className="Tilastot-credits-toggle"
        onClick={() => onToggle((prev) => !prev)}
        aria-expanded={!collapsed}
        aria-controls={listId}
      >
        <span className="Tilastot-credits-toggle-icon">
          {collapsed ? <ChevronRight size={20} aria-hidden /> : <ChevronDown size={20} aria-hidden />}
        </span>
        {children}
      </button>
    );
  }
  return (
    <div className="Tilastot-credits-toggle Tilastot-credits-toggle--static">
      <span className="Tilastot-credits-toggle-icon Tilastot-credits-toggle-icon--spacer" aria-hidden />
      {children}
    </div>
  );
}

const DAILY_ANSWER_BAR_COLORS = [
  '#22c55e',
  '#eab308',
  '#ea580c',
  '#991b1b',
  '#3b82f6',
  '#a855f7',
  '#14b8a6',
  '#db2777',
];

function DailyQuestionAnswerBar({ breakdown }) {
  const segments = (breakdown || []).filter((b) => b && Number(b.percent) > 0);
  const summary = segments.map((b) => `${b.label} ${b.percent}%`).join(', ');
  if (segments.length === 0) {
    return <div className="Tilastot-daily-bar-empty">Ei vastauksia</div>;
  }
  return (
    <div className="Tilastot-daily-bar" role="img" aria-label={summary}>
      {segments.map((b, i) => (
        <div
          key={`${b.label}-${i}`}
          className="Tilastot-daily-bar-seg"
          style={{
            flex: `0 0 ${b.percent}%`,
            backgroundColor: DAILY_ANSWER_BAR_COLORS[i % DAILY_ANSWER_BAR_COLORS.length],
          }}
          title={`${b.label}: ${b.percent}%`}
        >
          <span className="Tilastot-daily-bar-label">{b.label}</span>
          <span className="Tilastot-daily-bar-pct">{b.percent}%</span>
        </div>
      ))}
    </div>
  );
}

/** Free-form daily questions (no multi-option array): list distinct answers, no share bar. */
function DailyQuestionFreeformAnswerList({ breakdown }) {
  const rows = (breakdown || []).filter((b) => b && String(b.label || '').trim());
  if (rows.length === 0) {
    return <div className="Tilastot-daily-answers-plain-empty">Ei vastauksia</div>;
  }
  return (
    <ul className="Tilastot-daily-answers-plain-list">
      {rows.map((b, i) => (
        <li key={`${b.label}-${i}`} className="Tilastot-daily-answers-plain-item">
          {b.label}
          {Number(b.count) > 1 ? ` (${b.count})` : ''}
        </li>
      ))}
    </ul>
  );
}

function dailyQuestionHasOptionArray(q) {
  return Array.isArray(q?.options) && q.options.length >= 2;
}

function TilastotHeading({ showPlusBadge }) {
  const t = useT();
  return (
    <h2 className="Tilastot-page-heading">
      {t('stats.title')}
      {showPlusBadge ? (
        <span
          className="Tilastot-plus-badge"
          title="UNLOCK: Advanced Statistics"
          aria-label="Edistyneet tilastot (Statistics+)"
        >
          +
        </span>
      ) : null}
    </h2>
  );
}

export default function TilastotPage() {
  const { user } = useAuth();
  const t = useT();
  const { loading: statsGateLoading, unlocked: statsUnlocked } = useStatisticsUnlock(user?.id);
  const { loading: statsAdvancedProductLoading, unlocked: statsAdvancedUnlocked } =
    useCreditProductUnlocked(user?.id, STATISTICS_ADVANCED_UNLOCK_PRODUCT_ID);
  const [credits, setCredits] = useState([]);
  const [creditsLoading, setCreditsLoading] = useState(false);
  const [creditsBalance, setCreditsBalance] = useState(null);
  const [creditsCollapsed, setCreditsCollapsed] = useState(true);
  const [events, setEvents] = useState([]);
  const [eventsLoading, setEventsLoading] = useState(false);
  const [eventsCollapsed, setEventsCollapsed] = useState(true);
  const [forumPosts, setForumPosts] = useState([]);
  const [forumPostsTotal, setForumPostsTotal] = useState(null);
  const [forumPostsHasMore, setForumPostsHasMore] = useState(false);
  const [forumPostsLoading, setForumPostsLoading] = useState(false);
  const [forumPostsLoadingMore, setForumPostsLoadingMore] = useState(false);
  const [forumPostsCollapsed, setForumPostsCollapsed] = useState(true);
  const [dailyQuestions, setDailyQuestions] = useState([]);
  const [dailyQuestionsLoading, setDailyQuestionsLoading] = useState(false);
  const [dailyQuestionsCollapsed, setDailyQuestionsCollapsed] = useState(true);
  const [levyraatiHost, setLevyraatiHost] = useState([]);
  const [levyraatiLoading, setLevyraatiLoading] = useState(false);
  const [levyraatiCollapsed, setLevyraatiCollapsed] = useState(true);
  const [levyraatiExpandedEvents, setLevyraatiExpandedEvents] = useState(() => new Set());

  const forumPostsRef = useRef([]);
  const forumPostsLoadMoreLockRef = useRef(false);
  const forumScrollRef = useRef(null);
  const forumSentinelRef = useRef(null);
  const creditsScrollRef = useRef(null);
  const eventsScrollRef = useRef(null);
  const dailyScrollRef = useRef(null);
  const levyraatiScrollRef = useRef(null);
  const [visibleCounts, setVisibleCounts] = useState({
    credits: COLLAPSIBLE_PAGE_SIZE,
    events: COLLAPSIBLE_PAGE_SIZE,
    daily: COLLAPSIBLE_PAGE_SIZE,
    levyraati: COLLAPSIBLE_PAGE_SIZE,
  });
  const [activeScrollableKey, setActiveScrollableKey] = useState(null);
  const [showFloatingCollapse, setShowFloatingCollapse] = useState(false);

  const isForumMember = !!user?.role && user.role !== 'user';

  useEffect(() => {
    forumPostsRef.current = forumPosts;
  }, [forumPosts]);

  useEffect(() => {
    setVisibleCounts((prev) => ({ ...prev, credits: COLLAPSIBLE_PAGE_SIZE }));
  }, [credits.length]);

  useEffect(() => {
    setVisibleCounts((prev) => ({ ...prev, events: COLLAPSIBLE_PAGE_SIZE }));
  }, [events.length]);

  useEffect(() => {
    setVisibleCounts((prev) => ({ ...prev, daily: COLLAPSIBLE_PAGE_SIZE }));
  }, [dailyQuestions.length]);

  useEffect(() => {
    setVisibleCounts((prev) => ({ ...prev, levyraati: COLLAPSIBLE_PAGE_SIZE }));
  }, [levyraatiHost.length]);

  useEffect(() => {
    if (!user?.id) return;
    setCreditsLoading(true);
    const url = `${API}/api/users/${user.id}/credits/history`;
    fetch(url)
      .then((res) => (res.ok ? res.json() : []))
      .then((data) => setCredits(Array.isArray(data) ? data : []))
      .catch(() => setCredits([]))
      .finally(() => setCreditsLoading(false));
  }, [user?.id]);

  useEffect(() => {
    if (!user?.id) return;
    const userIdStr = String(user.id).replace(/n$/, '');
    fetch(`${API}/api/users/${userIdStr}/credits/balance`)
      .then((res) => (res.ok ? res.json() : { balance: 0 }))
      .then((data) => setCreditsBalance(data.balance != null ? Number(data.balance) : 0))
      .catch(() => setCreditsBalance(0));
  }, [user?.id]);

  useEffect(() => {
    if (!user?.id) return;
    setEventsLoading(true);
    const userIdStr = String(user.id).replace(/n$/, '');
    fetch(`${API}/api/events?withRatings=1&createdByUserId=${encodeURIComponent(userIdStr)}`)
      .then((res) => (res.ok ? res.json() : []))
      .then((data) => setEvents(Array.isArray(data) ? data : []))
      .catch(() => setEvents([]))
      .finally(() => setEventsLoading(false));
  }, [user?.id]);

  useEffect(() => {
    if (!user?.id) return;
    setDailyQuestionsLoading(true);
    const userIdStr = String(user.id).replace(/n$/, '');
    fetch(`${API}/api/users/${encodeURIComponent(userIdStr)}/daily-questions-authored-stats`)
      .then((res) => (res.ok ? res.json() : []))
      .then((data) => setDailyQuestions(Array.isArray(data) ? data : []))
      .catch(() => setDailyQuestions([]))
      .finally(() => setDailyQuestionsLoading(false));
  }, [user?.id]);

  useEffect(() => {
    if (!user?.id || !statsUnlocked || !statsAdvancedUnlocked) {
      setLevyraatiHost([]);
      setLevyraatiLoading(false);
      return;
    }
    setLevyraatiLoading(true);
    const userIdStr = String(user.id).replace(/n$/, '');
    fetch(`${API}/api/users/${encodeURIComponent(userIdStr)}/levyraati-host-stats`)
      .then((res) => (res.ok ? res.json() : Promise.reject()))
      .then((data) => setLevyraatiHost(Array.isArray(data) ? data : []))
      .catch(() => setLevyraatiHost([]))
      .finally(() => setLevyraatiLoading(false));
  }, [user?.id, statsUnlocked, statsAdvancedUnlocked]);

  const toggleLevyraatiEventExpanded = useCallback((eventId) => {
    setLevyraatiExpandedEvents((prev) => {
      const next = new Set(prev);
      if (next.has(eventId)) next.delete(eventId);
      else next.add(eventId);
      return next;
    });
  }, []);

  const fetchForumPostsPage = useCallback(
    (offset) => {
      const uid = String(user?.id || '');
      const url = `${API}/api/users/${encodeURIComponent(uid)}/forum-posts?limit=${FORUM_POSTS_PAGE_SIZE}&offset=${offset}`;
      return fetch(url).then((res) => (res.ok ? res.json() : Promise.reject(new Error('fetch'))));
    },
    [user?.id]
  );

  useEffect(() => {
    if (!user?.id || !isForumMember) {
      setForumPosts([]);
      setForumPostsTotal(null);
      setForumPostsHasMore(false);
      setForumPostsLoading(false);
      setForumPostsLoadingMore(false);
      return;
    }
    let cancelled = false;
    setForumPostsLoading(true);
    setForumPosts([]);
    setForumPostsTotal(null);
    setForumPostsHasMore(false);
    fetchForumPostsPage(0)
      .then((data) => {
        if (cancelled || !data) return;
        const list = Array.isArray(data.posts) ? data.posts : [];
        const totalNum = Number(data.total);
        setForumPosts(list);
        setForumPostsTotal(Number.isFinite(totalNum) ? totalNum : list.length);
        setForumPostsHasMore(!!data.hasMore);
      })
      .catch(() => {
        if (!cancelled) {
          setForumPosts([]);
          setForumPostsTotal(0);
          setForumPostsHasMore(false);
        }
      })
      .finally(() => {
        if (!cancelled) setForumPostsLoading(false);
      });
    return () => {
      cancelled = true;
      forumPostsLoadMoreLockRef.current = false;
    };
  }, [user?.id, isForumMember, fetchForumPostsPage]);

  const loadMoreForumPosts = useCallback(() => {
    if (!user?.id || !isForumMember || !forumPostsHasMore || forumPostsLoadMoreLockRef.current) return;
    forumPostsLoadMoreLockRef.current = true;
    const offset = forumPostsRef.current.length;
    setForumPostsLoadingMore(true);
    fetchForumPostsPage(offset)
      .then((data) => {
        if (!data) return;
        const list = Array.isArray(data.posts) ? data.posts : [];
        setForumPosts((prev) => {
          const seen = new Set(prev.map((p) => p.id));
          const merged = [...prev];
          for (const p of list) {
            if (p?.id && !seen.has(p.id)) {
              seen.add(p.id);
              merged.push(p);
            }
          }
          return merged;
        });
        setForumPostsHasMore(!!data.hasMore);
      })
      .catch(() => {})
      .finally(() => {
        forumPostsLoadMoreLockRef.current = false;
        setForumPostsLoadingMore(false);
      });
  }, [user?.id, isForumMember, forumPostsHasMore, fetchForumPostsPage]);

  const forumCollapsible =
    isForumMember && forumPostsTotal !== null && forumPostsTotal >= 1;
  const forumListCollapsed = forumCollapsible && forumPostsCollapsed;

  const dailyQuestionsSorted = [...dailyQuestions].sort((a, b) =>
    String(a.question || '').localeCompare(String(b.question || ''), 'fi'),
  );

  const dailyCount = dailyQuestionsSorted.length;
  const dailyCollapsible = !dailyQuestionsLoading && dailyCount >= 1;
  const dailyListCollapsed = dailyCollapsible && dailyQuestionsCollapsed;

  const creditsCollapsible = !creditsLoading && credits.length >= 1;
  const creditsListCollapsed = creditsCollapsible && creditsCollapsed;

  const eventsCollapsible = !eventsLoading && events.length >= 1;
  const eventsListCollapsed = eventsCollapsible && eventsCollapsed;

  const levyraatiCollapsible =
    statsAdvancedUnlocked && !levyraatiLoading && levyraatiHost.length >= 1;
  const levyraatiListCollapsed = levyraatiCollapsible && levyraatiCollapsed;

  const advancedStatsLoading =
    dailyQuestionsLoading ||
    statsAdvancedProductLoading ||
    (statsAdvancedUnlocked && levyraatiLoading);

  const visibleCredits = credits.slice(0, visibleCounts.credits);
  const visibleEvents = events.slice(0, visibleCounts.events);
  const visibleDailyQuestions = dailyQuestionsSorted.slice(0, visibleCounts.daily);
  const visibleLevyraatiHost = levyraatiHost.slice(0, visibleCounts.levyraati);

  const maybeGrowVisible = useCallback((key, totalLength) => {
    if (!totalLength || totalLength <= 0) return;
    setVisibleCounts((prev) => {
      const current = Number(prev[key]) || COLLAPSIBLE_PAGE_SIZE;
      if (current >= totalLength) return prev;
      return { ...prev, [key]: Math.min(totalLength, current + COLLAPSIBLE_PAGE_SIZE) };
    });
  }, []);

  const handleScrollableListScroll = useCallback(
    (key, totalLength, e) => {
      const el = e.currentTarget;
      if (!el) return;
      setActiveScrollableKey(key);
      setShowFloatingCollapse(el.scrollTop > 24);
      const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 140;
      if (nearBottom) maybeGrowVisible(key, totalLength);
    },
    [maybeGrowVisible]
  );

  const collapseActiveList = useCallback(() => {
    if (activeScrollableKey === 'credits') setCreditsCollapsed(true);
    else if (activeScrollableKey === 'events') setEventsCollapsed(true);
    else if (activeScrollableKey === 'daily') setDailyQuestionsCollapsed(true);
    else if (activeScrollableKey === 'levyraati') setLevyraatiCollapsed(true);
    else if (activeScrollableKey === 'forum') setForumPostsCollapsed(true);
    setShowFloatingCollapse(false);
  }, [activeScrollableKey]);

  useEffect(() => {
    if (forumListCollapsed || !isForumMember || !forumPostsHasMore) return undefined;
    const root = forumScrollRef.current;
    const target = forumSentinelRef.current;
    if (!root || !target) return undefined;
    const obs = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) loadMoreForumPosts();
      },
      { root, rootMargin: '100px', threshold: 0 }
    );
    obs.observe(target);
    return () => obs.disconnect();
  }, [forumListCollapsed, isForumMember, forumPostsHasMore, loadMoreForumPosts, forumPosts.length]);

  useEffect(() => {
    if (creditsListCollapsed && activeScrollableKey === 'credits') setShowFloatingCollapse(false);
  }, [creditsListCollapsed, activeScrollableKey]);
  useEffect(() => {
    if (eventsListCollapsed && activeScrollableKey === 'events') setShowFloatingCollapse(false);
  }, [eventsListCollapsed, activeScrollableKey]);
  useEffect(() => {
    if (dailyListCollapsed && activeScrollableKey === 'daily') setShowFloatingCollapse(false);
  }, [dailyListCollapsed, activeScrollableKey]);
  useEffect(() => {
    if (levyraatiListCollapsed && activeScrollableKey === 'levyraati') setShowFloatingCollapse(false);
  }, [levyraatiListCollapsed, activeScrollableKey]);
  useEffect(() => {
    if (forumListCollapsed && activeScrollableKey === 'forum') setShowFloatingCollapse(false);
  }, [forumListCollapsed, activeScrollableKey]);

  if (!user) {
    return (
      <div className="App-block">
        <div className="stats-container">
          <p>Kirjaudu sisään nähdäksesi tilastot.</p>
        </div>
      </div>
    );
  }

  if (statsGateLoading) {
    return (
      <div className="App-block">
        <div className="stats-container">
          <ProfileNavTopBar user={user} currentPage="stats" />
          <p>{loadingPageText(t, 'stats')}</p>
        </div>
      </div>
    );
  }

  if (!statsUnlocked) {
    return (
      <div className="App-block">
        <div className="stats-container">
          <ProfileNavTopBar user={user} currentPage="stats" />
          <TilastotHeading showPlusBadge={statsAdvancedUnlocked} />
          <p>
            Tilastot-sivu avautuu, kun olet ostanut tuotteen <strong>UNLOCK: Statistics</strong>{' '}
            Pulina-krediiteillä krediittikaupasta.
          </p>
          {user?.role && user.role !== 'user' ? (
            <p>
              <a href="/kauppa">Siirry kauppaan</a>
            </p>
          ) : (
            <p>Jäsenenä voit ostaa avauksen krediittikaupasta.</p>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="App-block">
      <div className="stats-container">
      <ProfileNavTopBar user={user} currentPage="stats" />
      <TilastotHeading showPlusBadge={statsAdvancedUnlocked} />

      <section className="Tilastot-credits">
        <TilastotCollapsibleHeader
          collapsible={creditsCollapsible}
          collapsed={creditsCollapsed}
          onToggle={setCreditsCollapsed}
          listId="Tilastot-credits-list"
        >
          <Coins className="Tilastot-credits-toggle-coins" size={22} strokeWidth={2} aria-hidden />
          <span className="Tilastot-credits-toggle-label">
            {creditsBalance != null ? `${creditsBalance} PC` : creditsLoading ? '…' : '0 PC'}
          </span>
        </TilastotCollapsibleHeader>
        <div
          id="Tilastot-credits-list"
          ref={creditsScrollRef}
          onScroll={(e) => handleScrollableListScroll('credits', credits.length, e)}
          className={`Tilastot-credits-list-wrap Tilastot-collapsible-scroll${
            creditsListCollapsed ? ' Tilastot-credits-list-wrap--collapsed' : ''
          }`}
        >
          {creditsLoading ? (
            <p className="Tilastot-credits-loading">Ladataan…</p>
          ) : credits.length === 0 ? (
            <p className="Tilastot-credits-empty">Ei krediittimerkintöjä.</p>
          ) : (
            <div className="Tilastot-credits-list">
              {visibleCredits.map((c, index) => (
                <div
                  key={c.id}
                  className={`Tilastot-credits-row ${index % 2 === 0 ? 'Tilastot-credits-row--even' : 'Tilastot-credits-row--odd'}`}
                >
                  <span className={creditAmountToneClass(c.earned)}>{formatCreditEarned(c.earned)}</span>{' '}
                  {displayReason(c.reason)}
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      <section className="Tilastot-credits Tilastot-events">
        <TilastotCollapsibleHeader
          collapsible={eventsCollapsible}
          collapsed={eventsCollapsed}
          onToggle={setEventsCollapsed}
          listId="Tilastot-events-list"
        >
          <BarChart2 className="Tilastot-credits-toggle-chart" size={22} strokeWidth={2} aria-hidden />
          <span className="Tilastot-credits-toggle-label">
            {eventsLoading ? '…' : formatStatsEventsCount(events.length, t)}
          </span>
        </TilastotCollapsibleHeader>
        <div
          id="Tilastot-events-list"
          ref={eventsScrollRef}
          onScroll={(e) => handleScrollableListScroll('events', events.length, e)}
          className={`Tilastot-credits-list-wrap Tilastot-collapsible-scroll${
            eventsListCollapsed ? ' Tilastot-credits-list-wrap--collapsed' : ''
          }`}
        >
          {eventsLoading ? (
            <p className="Tilastot-credits-loading">Ladataan…</p>
          ) : events.length === 0 ? (
            <p className="Tilastot-credits-empty">Ei tapahtumia.</p>
          ) : (
            <div className="Tilastot-credits-list">
              {visibleEvents.map((ev, index) => {
                const avg = ev.averageRating != null ? Number(ev.averageRating) : null;
                const count = ev.ratingsCount != null ? Number(ev.ratingsCount) : 0;
                const hasRatings = count > 0 && avg != null;
                const ratingText = hasRatings
                  ? `${avg}/5 ( ${count} )`
                  : ' - ei arvosteluja';
                return (
                  <div
                    key={ev.id}
                    className={`Tilastot-credits-row ${index % 2 === 0 ? 'Tilastot-credits-row--even' : 'Tilastot-credits-row--odd'}`}
                  >
                    <span className="Tilastot-event-main">
                      {ev.eventDate || ''} {ev.time || ''} —{' '}
                      <a
                        href={`/event/show/${encodeURIComponent(ev.id)}`}
                        className="Tilastot-event-link"
                      >
                        <EventTypeTitle event={ev} />
                      </a>{' '}
                      {hasRatings ? (
                        <span className="Tilastot-event-rating">
                          <Star size={14} fill="currentColor" strokeWidth={0} className="Tilastot-event-rating-star" aria-hidden />
                          {' '}{ratingText}
                        </span>
                      ) : (
                        ratingText
                      )}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </section>

      <section className="Tilastot-credits Tilastot-events Tilastot-forum">
        <TilastotCollapsibleHeader
          collapsible={forumCollapsible}
          collapsed={forumPostsCollapsed}
          onToggle={setForumPostsCollapsed}
          listId="Tilastot-forum-posts-list"
        >
          <MessageSquare className="Tilastot-credits-toggle-forum" size={22} strokeWidth={2} aria-hidden />
          <span className="Tilastot-credits-toggle-label">
            {!isForumMember
              ? 'Ilmoitustaulun viestit'
              : forumPostsLoading && forumPostsTotal === null
                ? '…'
                : forumPostsTotal !== null
                  ? formatStatsForumPostsHeader(forumPostsTotal, t)
                  : '…'}
          </span>
        </TilastotCollapsibleHeader>
        <div
          id="Tilastot-forum-posts-list"
          ref={forumScrollRef}
          onScroll={(e) => {
            setActiveScrollableKey('forum');
            setShowFloatingCollapse(e.currentTarget.scrollTop > 24);
          }}
          className={`Tilastot-credits-list-wrap Tilastot-forum-posts-scroll${
            forumListCollapsed ? ' Tilastot-credits-list-wrap--collapsed' : ''
          }`}
        >
          {!isForumMember ? (
            <p className="Tilastot-credits-empty">Ilmoitustaulu on vain jäsenille.</p>
          ) : forumPostsLoading && forumPosts.length === 0 ? (
            <p className="Tilastot-credits-loading">Ladataan…</p>
          ) : forumPosts.length === 0 ? (
            <p className="Tilastot-credits-empty">Ei ilmoituksia.</p>
          ) : (
            <>
              <div className="Tilastot-credits-list">
                {forumPosts.map((post, index) => {
                  const dateLabel = formatForumPostDate(post.postedAt || '');
                  const rawBody =
                    [post.postHeader, post.body, post.title]
                      .map((x) => (x != null ? String(x).trim() : ''))
                      .filter(Boolean)
                      .join(' — ') || '';
                  const snippet = truncateForumBody(rawBody, 20);
                  const replies = Number(post.repliesCount) || 0;
                  const unread = Number(post.unreadRepliesCount) || 0;
                  const replyPhrase = `${replies} ${replies === 1 ? 'vastaus' : 'vastausta'}`;
                  const unreadPhrase =
                    unread === 1 ? '1 lukematon' : `${unread} lukematonta`;
                  return (
                    <div
                      key={post.id}
                      className={`Tilastot-credits-row ${index % 2 === 0 ? 'Tilastot-credits-row--even' : 'Tilastot-credits-row--odd'}`}
                    >
                      <span className="Tilastot-forum-post-line">
                        {dateLabel}{' '}
                        <a href={`/forum/post/${encodeURIComponent(post.id)}`}>{snippet}</a>{' '}
                        {replyPhrase}{' '}
                        ({unread > 0 ? <strong>{unreadPhrase}</strong> : unreadPhrase})
                      </span>
                    </div>
                  );
                })}
              </div>
              {forumPostsHasMore ? (
                <div ref={forumSentinelRef} className="Tilastot-forum-sentinel" aria-hidden />
              ) : null}
              {forumPostsLoadingMore ? (
                <p className="Tilastot-credits-loading Tilastot-forum-load-more">Ladataan lisää…</p>
              ) : null}
            </>
          )}
        </div>
      </section>

      {advancedStatsLoading ? (
        <div
          className="advanced-stats advanced-stats--loading"
          aria-busy="true"
          aria-live="polite"
          aria-label="Ladataan edistyneitä tilastoja"
        >
          <span className="Tilastot-advanced-stats-plus-spinner" aria-hidden>
            +
          </span>
        </div>
      ) : (
      <div className="advanced-stats">
      <section className="Tilastot-credits Tilastot-daily-questions">
        <TilastotCollapsibleHeader
          collapsible={dailyCollapsible}
          collapsed={dailyQuestionsCollapsed}
          onToggle={setDailyQuestionsCollapsed}
          listId="Tilastot-daily-questions-list"
        >
          <CalendarDays className="Tilastot-credits-toggle-calendar" size={22} strokeWidth={2} aria-hidden />
          <span className="Tilastot-credits-toggle-label">
            {dailyQuestionsLoading ? '…' : formatStatsDailyPollsHeader(dailyCount, t)}
          </span>
        </TilastotCollapsibleHeader>
        <div
          id="Tilastot-daily-questions-list"
          ref={dailyScrollRef}
          onScroll={(e) => handleScrollableListScroll('daily', dailyQuestionsSorted.length, e)}
          className={`Tilastot-credits-list-wrap Tilastot-collapsible-scroll${
            dailyListCollapsed ? ' Tilastot-credits-list-wrap--collapsed' : ''
          }`}
        >
          {dailyQuestionsLoading ? (
            <p className="Tilastot-credits-loading">Ladataan…</p>
          ) : dailyQuestionsSorted.length === 0 ? (
            <p className="Tilastot-credits-empty">Ei omia päivän kysymyksiä.</p>
          ) : (
            <div className="Tilastot-daily-questions-list">
              {visibleDailyQuestions.map((q, index) => {
                const hasOptions = dailyQuestionHasOptionArray(q);
                const vastauksiaN = Number(q.totalAnswers);
                const vastauksiaDisplay = Number.isFinite(vastauksiaN) ? vastauksiaN : 0;
                return (
                  <div
                    key={q.id}
                    className={`Tilastot-daily-question-row ${index % 2 === 0 ? 'Tilastot-daily-question-row--even' : 'Tilastot-daily-question-row--odd'}`}
                  >
                    <div
                      className={`Tilastot-daily-question-row-inner${
                        hasOptions ? '' : ' Tilastot-daily-question-row-inner--plain-answers'
                      }`}
                    >
                      <p className="Tilastot-daily-question-text">
                        <span className="Tilastot-daily-question-body">{q.question}</span>
                        <span className="Tilastot-daily-vastauksia-count">Vastauksia: {vastauksiaDisplay}</span>
                      </p>
                      <div className="Tilastot-daily-bar-wrap">
                        {hasOptions ? (
                          <DailyQuestionAnswerBar breakdown={q.breakdown} />
                        ) : (
                          <DailyQuestionFreeformAnswerList breakdown={q.breakdown} />
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {statsAdvancedUnlocked ? (
        <section className="Tilastot-credits Tilastot-levyraati-host">
          <TilastotCollapsibleHeader
            collapsible={levyraatiCollapsible}
            collapsed={levyraatiCollapsed}
            onToggle={setLevyraatiCollapsed}
            listId="Tilastot-levyraati-host-list"
          >
            <Disc3 className="Tilastot-credits-toggle-levyraati" size={22} strokeWidth={2} aria-hidden />
            <span className="Tilastot-credits-toggle-label">
              {levyraatiLoading ? '…' : formatStatsMusicReviewsHeader(levyraatiHost.length, t)}
            </span>
          </TilastotCollapsibleHeader>
          <div
            id="Tilastot-levyraati-host-list"
            ref={levyraatiScrollRef}
            onScroll={(e) => handleScrollableListScroll('levyraati', levyraatiHost.length, e)}
            className={`Tilastot-credits-list-wrap Tilastot-collapsible-scroll${
              levyraatiListCollapsed ? ' Tilastot-credits-list-wrap--collapsed' : ''
            }`}
          >
            {levyraatiLoading ? (
              <p className="Tilastot-credits-loading">Ladataan…</p>
            ) : levyraatiHost.length > 0 ? (
              <div className="Tilastot-levyraati-events">
                {visibleLevyraatiHost.map((ev, evIndex) => {
                  const fbList = Array.isArray(ev.feedbacks) ? ev.feedbacks : [];
                  const fbCount = fbList.length;
                  const expanded = levyraatiExpandedEvents.has(ev.id);
                  const eventBodyCollapsed = !expanded;
                  const when =
                    [ev.eventDate, ev.time].filter(Boolean).join(' ') || '—';
                  return (
                    <div
                      key={ev.id}
                      className={`Tilastot-levyraati-event Tilastot-levyraati-event--has-feedback ${
                        evIndex % 2 === 0
                          ? 'Tilastot-levyraati-event--has-feedback-even'
                          : 'Tilastot-levyraati-event--has-feedback-odd'
                      }`}
                    >
                      <button
                        type="button"
                        className="Tilastot-levyraati-event-toggle"
                        onClick={() => toggleLevyraatiEventExpanded(ev.id)}
                        aria-expanded={expanded}
                        aria-controls={`Tilastot-levyraati-fb-${ev.id}`}
                      >
                        <span className="Tilastot-levyraati-event-chevron">
                          {expanded ? (
                            <ChevronDown size={18} aria-hidden />
                          ) : (
                            <ChevronRight size={18} aria-hidden />
                          )}
                        </span>
                        <span className="Tilastot-levyraati-event-summary">
                          <span className="Tilastot-levyraati-event-title">{ev.title || '—'}</span>
                          <span className="Tilastot-levyraati-event-meta">
                            {when} · {fbCount} {fbCount === 1 ? 'palaute' : 'palautetta'}
                          </span>
                        </span>
                      </button>
                      <div
                        id={`Tilastot-levyraati-fb-${ev.id}`}
                        className={`Tilastot-levyraati-event-body${
                          eventBodyCollapsed ? ' Tilastot-levyraati-event-body--collapsed' : ''
                        }`}
                      >
                        <ul className="Tilastot-levyraati-fb-list">
                          {fbList.map((f) => {
                            const who =
                              f.authorDisplayName || f.authorUsername || '—';
                            const created =
                              f.createdAt != null
                                ? new Date(f.createdAt).toLocaleString('fi-FI')
                                : '';
                            return (
                              <li key={f.id} className="Tilastot-levyraati-fb-item">
                                <div className="Tilastot-levyraati-fb-track">
                                  {f.trackAuthor || '—'} — {f.trackName || '—'}
                                </div>
                                <div className="Tilastot-levyraati-fb-who">
                                  {who}
                                  {created ? ` · ${created}` : ''}
                                </div>
                                {f.rating != null && Number.isFinite(Number(f.rating)) ? (
                                  <div className="Tilastot-levyraati-fb-rating">
                                    <Star
                                      size={14}
                                      fill="currentColor"
                                      strokeWidth={0}
                                      className="Tilastot-event-rating-star"
                                      aria-hidden
                                    />{' '}
                                    {Number(f.rating)}/5
                                  </div>
                                ) : null}
                                {f.feedback && String(f.feedback).trim() ? (
                                  <div className="Tilastot-levyraati-fb-text">{f.feedback}</div>
                                ) : null}
                              </li>
                            );
                          })}
                        </ul>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : null}
          </div>
        </section>
      ) : null}
      </div>
      )}
      {showFloatingCollapse && activeScrollableKey ? (
        <button
          type="button"
          className="Tilastot-floating-collapse-btn"
          onClick={collapseActiveList}
          aria-label="Sulje avoin listaus"
          title="Sulje"
        >
          <X size={20} aria-hidden />
        </button>
      ) : null}
      </div>
    </div>
  );
}

